import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API_URL, buildKioskAuthHeaders } from './api';
import { PRODUCT_IDENTITIES, productForLocation } from './productIdentity';
import './ProductIdentity.css';
import './ProductOptions.css';
import ProductOffers from './ProductOffers';
import ProductContracts from './ProductContracts';

const MODULES = {
  beborne: { name: 'Bornes BEKIOSK', description: 'Commande sur borne tactile.', path: '/client', action: 'Ouvrir la borne' },
  becaisse: { name: 'BECAISSE', description: 'Encaissement et prise de commande.', path: '/caisse', action: 'Ouvrir la caisse' },
  becaisse_pad: { name: 'PAD', description: 'Prise de commande mobile en salle.', path: '/pad', action: 'Ouvrir le PAD' },
  kds: { name: 'Cuisine / KDS', description: 'Préparation et suivi des commandes.', path: '/cuisine', action: 'Ouvrir la cuisine' },
  befid: { name: 'Écrans de fidélité', description: 'Droit au module d’écrans de fidélité existant.' },
  bemenu: { name: 'BEMenu', description: 'Menus et commande à table.' },
  beclick: { name: 'BECLICK', description: 'Commande en ligne et Click & Collect.' },
  beresa: { name: 'Réservation', description: 'Réservations de votre établissement.' },
};
const ORDERS = {
  becaisse: ['becaisse', 'becaisse_pad', 'kds', 'beborne'],
  bekiosk: ['beborne', 'kds', 'becaisse', 'becaisse_pad', 'beclick', 'bemenu', 'beresa'],
  beresto: ['becaisse', 'becaisse_pad', 'beborne', 'kds', 'beclick', 'beresa', 'bemenu'],
};
const SHOPS = { becaisse: 'https://becaisse.fr/boutique.html', bekiosk: 'https://beborne.com/boutique.html', beresto: 'https://beresto.fr/boutique.html' };
const EXTERNALS = { bestock: 'BESTOCK', bequality: 'BEQUALITY', becost: 'BECOST', betime: 'BETIME' };
const CONFIGURATION_LABELS = {
  ready: 'Configuration enregistrée',
  pending: 'Configuration en attente',
  disabled: 'Configuration désactivée',
  unknown: 'Configuration inconnue',
};
const isCount = value => value === null || (Number.isInteger(value) && value >= 0);
function validPayload(payload) {
  return payload?.schemaVersion === 1 && Array.isArray(payload.modules) && Array.isArray(payload.externalServices)
    && payload.modules.every(item => item && typeof item.id === 'string'
      && [true, false, null].includes(item.enabled) && isCount(item.limit) && isCount(item.registeredActive))
    && payload.externalServices.every(item => item && typeof item.id === 'string'
      && [true, false, null].includes(item.enabled) && Object.prototype.hasOwnProperty.call(CONFIGURATION_LABELS, item.provisioningStatus));
}
function AccessLabel({ enabled }) {
  const label = enabled === true ? 'Accès autorisé' : enabled === false ? 'Accès non autorisé' : 'État indisponible';
  return <span className={`product-options-status ${enabled === true ? 'is-authorized' : 'is-neutral'}`}>{label}</span>;
}

function ModuleCard({ module }) {
  const definition = MODULES[module.id];
  return <article className="product-options-card" key={module.id} aria-label={definition.name}>
    <AccessLabel enabled={module.enabled} />
    <h3>{definition.name}</h3><p>{definition.description}</p>
    {!['beclick', 'beresa'].includes(module.id) && <dl className="product-options-counts">
      <div><dt>Quota de postes</dt><dd>{module.limit === null ? 'Non renseigné' : module.limit}</dd></div>
      <div><dt>Postes enregistrés actifs</dt><dd>{module.registeredActive === null ? 'Non renseigné' : module.registeredActive}</dd></div>
    </dl>}
    {module.enabled === true && definition.path && <Link className="product-options-open" to={definition.path}>{definition.action} <span aria-hidden="true">↗</span></Link>}
  </article>;
}

export default function ProductOptions() {
  const location = useLocation();
  const product = productForLocation(location);
  const identity = PRODUCT_IDENTITIES[product];
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading' });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading' });
    async function load() {
      try {
        const response = await fetch(`${API_URL}/api/admin/options`, {
          method: 'GET', headers: buildKioskAuthHeaders(), signal: controller.signal,
        });
        if (!active) return;
        if ([401, 402, 403].includes(response.status)) {
          let status = { 401: 'unauthorized', 402: 'restricted', 403: 'forbidden' }[response.status];
          if (response.status === 403) {
            // The server also uses 403 when the establishment's access is suspended.
            try {
              const denied = await response.json();
              if (denied?.code === 'SUBSCRIPTION_INACTIVE') status = 'restricted';
            } catch (_) { /* A missing or invalid error body remains a bounded denial. */ }
          }
          if (active) setState({ status });
          return;
        }
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        if (!validPayload(data)) throw new Error('invalid-response');
        if (active) setState({ status: 'loaded', data });
      } catch (_) {
        if (active) setState({ status: 'error' });
      }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [attempt]);

  const modules = ORDERS[product].map(id => state.data?.modules.find(item => item.id === id)).filter(Boolean);
  const otherModules = Object.keys(MODULES).filter(id => !ORDERS[product].includes(id))
    .map(id => state.data?.modules.find(item => item.id === id)).filter(Boolean);
  const externalServices = Object.keys(EXTERNALS).map(id => state.data?.externalServices.find(item => item.id === id)).filter(Boolean);
  const hasOptions = modules.length > 0 || otherModules.length > 0 || externalServices.length > 0;
  return <main className={`product-theme product-theme--${product} product-options-page`}>
    <div className="product-options-shell">
      <header className="product-options-header">
        <picture className="product-options-logo">
          <source media="(max-width: 680px)" srcSet={`/static/branding-v5/${product}/compact.svg`} />
          <img src={`/static/branding-v4/${product}/horizontal.svg`} alt={identity.name} />
        </picture>
        <Link className="product-options-back" to={`/admin/select?product=${product}`}>← Mon espace</Link>
      </header>
      <div className="product-options-intro">
        <span className="product-options-eyebrow">{identity.workspace}</span>
        <h1>Mes options</h1>
        <p>Retrouvez les accès et les postes de votre établissement, ainsi que la configuration de vos applications complémentaires.</p>
      </div>
      {state.status === 'loading' && <div className="product-options-message" role="status">Chargement de vos options…</div>}
      {state.status === 'error' && <div className="product-options-message" role="alert">
        <h2>Vos options sont momentanément indisponibles</h2>
        <p>Nous n’avons pas pu récupérer leurs états. Vos droits ne sont pas modifiés.</p>
        <button type="button" onClick={() => setAttempt(value => value + 1)}>Réessayer</button>
      </div>}
      {state.status === 'unauthorized' && <div className="product-options-message" role="alert">
        <h2>Votre session a expiré</h2><p>Reconnectez-vous pour consulter vos options.</p>
        <Link to={`/admin?product=${product}`} state={{ from: location }}>Se reconnecter</Link>
      </div>}
      {state.status === 'restricted' && <div className="product-options-message" role="alert">
        <h2>Accès temporairement restreint</h2><p>Les options ne peuvent pas être consultées avec l’accès actuel de cet établissement.</p>
      </div>}
      {state.status === 'forbidden' && <div className="product-options-message" role="alert">
        <h2>Accès administrateur requis</h2><p>Seul un administrateur de l’établissement peut consulter ces informations.</p>
      </div>}
      {state.status === 'loaded' && <>
        {!hasOptions && <div className="product-options-message" role="status">Aucune information disponible pour cet établissement.</div>}
        {modules.length > 0 && <section className="product-options-section" aria-labelledby="product-options-modules">
          <div className="product-options-section-title"><h2 id="product-options-modules">Vos outils au quotidien</h2><p>Les droits effectifs de votre établissement.</p></div>
          <div className="product-options-grid">{modules.map(module => <ModuleCard key={module.id} module={module} />)}</div>
          <p className="product-options-footnote">Les postes enregistrés ne sont pas nécessairement connectés. Les droits affichés ne constituent pas un état de vos abonnements.</p>
        </section>}
        {otherModules.length > 0 && <section className="product-options-section product-options-section--secondary" aria-labelledby="product-options-other">
          <div className="product-options-section-title"><h2 id="product-options-other">Autres outils de la suite BE</h2><p>Retrouvez également les droits de votre établissement pour ces outils.</p></div>
          <div className="product-options-grid">{otherModules.map(module => <ModuleCard key={module.id} module={module} />)}</div>
          <p className="product-options-footnote">Le module d’écrans de fidélité est distinct de l’offre commerciale de fidélisation.</p>
        </section>}
        {externalServices.length > 0 && <section className="product-options-section" aria-labelledby="product-options-external">
          <div className="product-options-section-title"><h2 id="product-options-external">Vos applications complémentaires</h2><p>La configuration actuellement enregistrée pour votre établissement.</p></div>
          <div className="product-options-grid product-options-grid--external">{externalServices.map(service => <article className="product-options-card" key={service.id} aria-label={EXTERNALS[service.id]}>
            <AccessLabel enabled={service.enabled} /><h3>{EXTERNALS[service.id]}</h3>
            <p className="product-options-configuration">{CONFIGURATION_LABELS[service.provisioningStatus]}</p>
          </article>)}</div>
          <p className="product-options-footnote">Une configuration enregistrée ne confirme pas la disponibilité de l’application distante.</p>
        </section>}
        <ProductContracts />
        <ProductOffers product={product} />
      </>}
      <aside className="product-options-discover" aria-labelledby="product-options-discover">
        <div><span className="product-options-eyebrow">Pour aller plus loin</span><h2 id="product-options-discover">Des options pour votre activité</h2>
          <p>Découvrez les offres et les options sur devis de {identity.name}. Les offres de fidélisation et de KDS par écran restent distinctes des droits aux modules affichés ici.</p></div>
        <a href={SHOPS[product]} target="_blank" rel="noopener noreferrer">Découvrir les offres <span aria-hidden="true">↗</span><span className="product-options-sr-only"> (nouvel onglet)</span></a>
      </aside>
    </div>
  </main>;
}
